import React, { useEffect, useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, IdCell, Input, KV, Modal, PageHead,
  SearchBox, Switch, Textarea, inr, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { brandApi, categoryApi, companyApi, productApi, productExtrasApi } from '../api';
import { useToast } from '../context/ToastContext';

const emptyForm = {
  name: '', sku: '', hsnCode: '6306', shortDescription: '', description: '',
  manufacturer: '', brand: '', category: '', unit: 'piece',
  basePrice: '', costPrice: '', mrp: '', gstRate: 18, moq: 1, visibleQty: 1, leadTimeDays: 3,
  weightKg: '', returnPolicy: 'return_or_replacement',
  specifications: { material: 'HDPE', gsm: '', color: '', waterproof: true, uvTreated: false, fireRetardant: false, warrantyMonths: 0 },
  bulkPricing: [],
  isFeatured: false, isActive: true, requiresAuthorization: false,
};

const UNITS = ['piece', 'kg', 'roll', 'meter', 'sq_ft', 'bundle'];
const MATERIALS = ['HDPE', 'PVC', 'Canvas', 'PE', 'Polyester'];

const Products = () => {
  const toast = useToast();
  const list = useList(productApi.list, { search: '', category: '', manufacturer: '', isActive: '', sort: 'newest' });

  const [refs, setRefs] = useState({ categories: [], brands: [], manufacturers: [] });
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [remove, setRemove] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      categoryApi.list({ all: 'true' }),
      brandApi.list({ limit: 100 }),
      companyApi.list({ type: 'manufacturer', verificationStatus: 'approved', limit: 100 }),
    ]).then(([cats, brands, mfgs]) =>
      setRefs({
        categories: cats.status === 'fulfilled' ? cats.value.data : [],
        brands: brands.status === 'fulfilled' ? brands.value.data : [],
        manufacturers: mfgs.status === 'fulfilled' ? mfgs.value.data : [],
      }),
    );
  }, []);

  const setSpec = (key, value) =>
    setForm(prev => ({ ...prev, specifications: { ...prev.specifications, [key]: value } }));

  const addSlab = () =>
    setForm(prev => ({ ...prev, bulkPricing: [...prev.bulkPricing, { minQty: '', price: '', label: '' }] }));

  const setSlab = (index, key, value) =>
    setForm(prev => ({
      ...prev,
      bulkPricing: prev.bulkPricing.map((slab, i) => (i === index ? { ...slab, [key]: value } : slab)),
    }));

  const removeSlab = index =>
    setForm(prev => ({ ...prev, bulkPricing: prev.bulkPricing.filter((unused, i) => i !== index) }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Give the product a name');
    if (!form.category) return toast.error('Pick a category');
    if (!form.manufacturer) return toast.error('Pick the manufacturer');
    if (!form.basePrice) return toast.error('Set a base price');

    setBusy(true);
    try {
      const payload = {
        ...form,
        basePrice: Number(form.basePrice),
        mrp: Number(form.mrp) || 0,
        costPrice: Number(form.costPrice) || 0,
        moq: Number(form.moq) || 1,
        visibleQty: Number(form.visibleQty) || 1,
        // Delhivery rates on weight, so a listing without one falls back to the
        // platform default and the buyer's delivery estimate drifts.
        weightKg: Number(form.weightKg) || 0,
        brand: form.brand || undefined,
        specifications: {
          ...form.specifications,
          gsm: form.specifications.gsm ? Number(form.specifications.gsm) : null,
        },
        bulkPricing: form.bulkPricing
          .filter(s => s.minQty && s.price)
          .map(s => ({ minQty: Number(s.minQty), price: Number(s.price), label: s.label || `${s.minQty}+ units` })),
      };

      if (form._id) await productApi.update(form._id, payload);
      else await productApi.create(payload);

      toast.success(form._id ? 'Product updated' : 'Product created');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  /** One click from the list, rather than opening the whole edit form. */
  const toggleFeatured = async row => {
    try {
      const res = await productExtrasApi.setFeatured(row._id, !row.isFeatured);
      toast.success(res.message);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const togglePublished = async (row, isActive) => {
    try {
      await productApi.toggle(row._id, isActive);
      toast.success(isActive ? 'Product published' : 'Product unpublished');
      list.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const doRemove = async () => {
    setBusy(true);
    try {
      await productApi.remove(remove._id);
      toast.success('Product deleted');
      setRemove(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openEdit = row => {
    setForm({
      ...emptyForm,
      ...row,
      manufacturer: row.manufacturer?._id || row.manufacturer || '',
      brand: row.brand?._id || row.brand || '',
      category: row.category?._id || row.category || '',
      specifications: { ...emptyForm.specifications, ...(row.specifications || {}) },
      bulkPricing: row.bulkPricing || [],
    });
  };

  const columns = [
    {
      key: 'name',
      label: 'Product',
      render: row => <IdCell image={row.thumbnail} name={row.name} sub={`${row.sku || '—'} · ${row.brand?.name || 'No brand'}`} />,
    },
    { key: 'category', label: 'Category', render: row => row.category?.name || '—' },
    { key: 'manufacturer', label: 'Manufacturer', render: row => <span className="text-sm">{row.manufacturer?.name || '—'}</span> },
    {
      key: 'basePrice',
      label: 'Price',
      align: 'right',
      render: row => (
        <>
          <div className="cell-strong">{inr(row.basePrice)}</div>
          {row.mrp > row.basePrice && <div className="cell-muted" style={{ textDecoration: 'line-through' }}>{inr(row.mrp)}</div>}
        </>
      ),
    },
    {
      key: 'totalStock',
      label: 'Stock',
      align: 'right',
      render: row => (
        <>
          <div className={row.totalStock > 0 ? 'cell-strong' : ''} style={{ color: row.totalStock > 0 ? undefined : 'var(--danger)' }}>
            {num(row.totalStock)}
          </div>
          {row.reservedStock > 0 && <div className="cell-muted">{num(row.reservedStock)} reserved</div>}
        </>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: row => (
        <div className="row" style={{ gap: 5 }}>
          {row.isFeatured && <Badge status="featured" tone="brand" dot={false}>★</Badge>}
          {row.requiresAuthorization && <Badge status="restricted" tone="warn" dot={false}>Restricted</Badge>}
          <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Live' : 'Draft'}</Badge>
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button
            size="sm"
            variant={row.isFeatured ? 'primary' : 'ghost'}
            title={row.isFeatured ? 'Remove from the app home screen' : 'Feature on the app home screen'}
            onClick={e => { e.stopPropagation(); toggleFeatured(row); }}
          >
            {row.isFeatured ? '★ Featured' : '☆ Feature'}
          </Button>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant={row.isActive ? 'ghost' : 'soft'} onClick={e => { e.stopPropagation(); togglePublished(row, !row.isActive); }}>
            {row.isActive ? 'Unpublish' : 'Publish'}
          </Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setRemove(row); }}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Products"
        subtitle="The full tarpaulin catalogue across every manufacturer."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New product</Button>}
      />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search by name or SKU…" />
        <select className="select" style={{ width: 190 }} value={list.filters.category} onChange={e => list.setFilter('category', e.target.value)}>
          <option value="">All categories</option>
          {refs.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select className="select" style={{ width: 210 }} value={list.filters.manufacturer} onChange={e => list.setFilter('manufacturer', e.target.value)}>
          <option value="">All manufacturers</option>
          {refs.manufacturers.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
        <select className="select" style={{ width: 150 }} value={list.filters.isActive} onChange={e => list.setFilter('isActive', e.target.value)}>
          <option value="">Any status</option>
          <option value="true">Live</option>
          <option value="false">Draft</option>
        </select>
        <select className="select" style={{ width: 160 }} value={list.filters.sort} onChange={e => list.setFilter('sort', e.target.value)}>
          <option value="newest">Newest first</option>
          <option value="popular">Best selling</option>
          <option value="price_low">Price: low → high</option>
          <option value="price_high">Price: high → low</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        loading={list.loading}
        meta={list.meta}
        onPage={list.setPage}
        onRowClick={async row => {
          try {
            setDetail((await productApi.get(row._id)).data);
          } catch (err) {
            toast.error(err.message);
          }
        }}
      />

      {/* --------------------------------------------------------- editor */}
      <Modal
        open={!!form}
        size="wide"
        title={form?._id ? 'Edit product' : 'New product'}
        subtitle={form?._id ? form.sku : 'Add a tarpaulin to the catalogue'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Create product'}</Button>
          </>
        }
      >
        {form && (
          <>
            <div className="card-title mb-2">Basics</div>
            <Input label="Product name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Tarpex Shield HDPE Tarpaulin 200 GSM" />
            <div className="grid-2">
              <Input label="SKU" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value.toUpperCase() })} placeholder="TRP-HDPE-200" />
              <Input label="HSN code" value={form.hsnCode} onChange={e => setForm({ ...form, hsnCode: e.target.value })} hint="Tarpaulins are usually 6306." />
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Manufacturer</label>
                <select className="select" value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })}>
                  <option value="">Select…</option>
                  {refs.manufacturers.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Category</label>
                <select className="select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="">Select…</option>
                  {refs.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Brand</label>
                <select className="select" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })}>
                  <option value="">No brand</option>
                  {refs.brands
                    .filter(b => !form.manufacturer || (b.manufacturer?._id || b.manufacturer) === form.manufacturer)
                    .map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Selling unit</label>
                <select className="select" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map(u => <option key={u} value={u}>{u.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>

            <Input label="Short description" value={form.shortDescription} onChange={e => setForm({ ...form, shortDescription: e.target.value })} placeholder="100% waterproof, UV-treated HDPE tarpaulin" />
            <Textarea label="Full description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />

            <div className="divider" />
            <div className="card-title mb-2">Pricing</div>
            <div className="grid-2">
              <Input label="Cost price (₹)" type="number" value={form.costPrice} onChange={e => setForm({ ...form, costPrice: e.target.value })} hint="Private — never sent to buyers." />
              <Input label="Base price (₹)" type="number" value={form.basePrice} onChange={e => setForm({ ...form, basePrice: e.target.value })} />
              <Input label="MRP (₹)" type="number" value={form.mrp} onChange={e => setForm({ ...form, mrp: e.target.value })} hint="Shown struck through when higher." />
              <Input label="GST rate (%)" type="number" value={form.gstRate} onChange={e => setForm({ ...form, gstRate: Number(e.target.value) })} />
              <Input label="Minimum order qty" type="number" min={1} value={form.moq} onChange={e => setForm({ ...form, moq: e.target.value })} hint="Adding to cart fills this quantity." />
              <Input label="Visible qty" type="number" min={1} value={form.visibleQty} onChange={e => setForm({ ...form, visibleQty: e.target.value })} hint="Quantity the listed price is quoted for." />
              <Input label="Weight per unit (kg)" type="number" step="0.01" value={form.weightKg} onChange={e => setForm({ ...form, weightKg: e.target.value })} hint="Drives the courier's delivery charge." />

              <div className="field">
                <label>Return policy</label>
                <select className="select" value={form.returnPolicy} onChange={e => setForm({ ...form, returnPolicy: e.target.value })}>
                  <option value="return_or_replacement">100% Return or Replacement</option>
                  <option value="return">100% Return</option>
                  <option value="replacement">Replacement only</option>
                  <option value="no_return">No return</option>
                </select>
              </div>
            </div>

            <div className="row-between mb-2">
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Bulk price slabs</div>
                <div className="text-muted text-sm">Buying more drops the unit price automatically.</div>
              </div>
              <Button size="sm" variant="soft" onClick={addSlab}>＋ Add slab</Button>
            </div>

            {form.bulkPricing.map((slab, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="row mb-2" style={{ gap: 8 }}>
                <input className="input" type="number" placeholder="Min qty" value={slab.minQty} onChange={e => setSlab(index, 'minQty', e.target.value)} />
                <input className="input" type="number" placeholder="Price ₹" value={slab.price} onChange={e => setSlab(index, 'price', e.target.value)} />
                <input className="input" placeholder="Label" value={slab.label} onChange={e => setSlab(index, 'label', e.target.value)} />
                <Button size="sm" variant="danger" onClick={() => removeSlab(index)}>✕</Button>
              </div>
            ))}

            <div className="divider" />
            <div className="card-title mb-2">Specifications</div>
            <div className="grid-2">
              <div className="field">
                <label>Material</label>
                <select className="select" value={form.specifications.material} onChange={e => setSpec('material', e.target.value)}>
                  {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <Input label="GSM" type="number" value={form.specifications.gsm} onChange={e => setSpec('gsm', e.target.value)} placeholder="200" />
              <Input label="Colour" value={form.specifications.color} onChange={e => setSpec('color', e.target.value)} placeholder="Blue / Silver" />
              <Input label="Warranty (months)" type="number" value={form.specifications.warrantyMonths} onChange={e => setSpec('warrantyMonths', Number(e.target.value))} />
              <Input label="Lead time (days)" type="number" value={form.leadTimeDays} onChange={e => setForm({ ...form, leadTimeDays: Number(e.target.value) })} />
            </div>

            <div className="col" style={{ gap: 11 }}>
              <Switch checked={form.specifications.waterproof} onChange={v => setSpec('waterproof', v)} label="Waterproof" />
              <Switch checked={form.specifications.uvTreated} onChange={v => setSpec('uvTreated', v)} label="UV treated" />
              <Switch checked={form.specifications.fireRetardant} onChange={v => setSpec('fireRetardant', v)} label="Fire retardant" />
            </div>

            <div className="divider" />
            <div className="col" style={{ gap: 11 }}>
              <Switch checked={form.isFeatured} onChange={v => setForm({ ...form, isFeatured: v })} label="Feature on the app home screen" />
              <Switch
                checked={form.requiresAuthorization}
                onChange={v => setForm({ ...form, requiresAuthorization: v })}
                label="Only authorized dealers may order this product"
              />
              <Switch checked={form.isActive} onChange={v => setForm({ ...form, isActive: v })} label="Publish to buyers" />
            </div>
          </>
        )}
      </Modal>

      {/* --------------------------------------------------------- detail */}
      <Modal
        open={!!detail}
        size="wide"
        title={detail?.name}
        subtitle={detail ? `${detail.sku || ''} · ${detail.manufacturer?.name || ''}` : ''}
        onClose={() => setDetail(null)}
        footer={detail && <Button onClick={() => { openEdit(detail); setDetail(null); }}>Edit product</Button>}
      >
        {detail && (
          <>
            {(detail.images || []).length > 0 && (
              <div className="row wrap mb-2">
                {detail.images.map(src => (
                  <img key={src} src={src} alt="" style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--line)' }} />
                ))}
              </div>
            )}

            <KV
              items={[
                { label: 'Base price', value: inr(detail.basePrice) },
                { label: 'MRP', value: inr(detail.mrp) },
                { label: 'GST', value: `${detail.gstRate}%` },
                { label: 'MOQ', value: `${detail.moq} ${detail.unit}` },
                { label: 'Visible qty', value: `${detail.visibleQty || 1} ${detail.unit}` },
                { label: 'Weight', value: detail.weightKg ? `${detail.weightKg} kg` : '—' },
                { label: 'Return policy', value: detail.returnPolicyLabel || '—' },
                { label: 'Stock', value: `${num(detail.totalStock)} total · ${num(detail.reservedStock)} reserved` },
                { label: 'Sold', value: num(detail.totalSold) },
                { label: 'Material', value: detail.specifications?.material || '—' },
                { label: 'GSM', value: detail.specifications?.gsm || '—' },
                { label: 'Colour', value: detail.specifications?.color || '—' },
                { label: 'Lead time', value: `${detail.leadTimeDays} days` },
              ]}
            />

            {(detail.bulkPricing || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Bulk pricing</div>
                {detail.bulkPricing.map(slab => (
                  <div className="row-between text-sm" key={slab.minQty} style={{ padding: '5px 0' }}>
                    <span>{slab.label || `${slab.minQty}+ units`}</span>
                    <strong>{inr(slab.price)} / {detail.unit}</strong>
                  </div>
                ))}
              </>
            )}

            {detail.description && (
              <>
                <div className="divider" />
                <p className="text-sm text-muted">{detail.description}</p>
              </>
            )}
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!remove}
        danger
        title="Delete this product?"
        message={`"${remove?.name}" will be permanently removed from the catalogue.`}
        confirmLabel="Delete"
        loading={busy}
        onConfirm={doRemove}
        onClose={() => setRemove(null)}
      />
    </>
  );
};

export default Products;
